/* eslint-disable no-unused-vars */
import { ApolloQueryResult } from '@apollo/client';
import ButtonGroup from '@atlaskit/button/button-group';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import Form, { Field, FormApi, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import get from 'lodash/get';
import set from 'lodash/set';
import * as React from 'react';
import { isPresent, AddOns, UpdateOrderRequest, AddOnSelectItem } from '@atlassian/cssxp-typescommon';
import { InvoiceGroup, Maybe, BillingFrequency, CycleInterval } from '@css-xp/graphtypes';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import StatuspageAvailableOfferingsFormField from '../../entitlement-offerings/src/statuspage-available-offerings-form-field';
import { TransactionAccountContextType } from '@css-xp/base/common-context';
//import { Sidebar } from ''; To:Do Need to import from 'statuspage.tsx'
import './entitlement-annual-subscription-pane.scss';
import {
  useFormSubmit,
  useNotification,
  orderSuccessNotification,
  SelectPromotionFormField,
  InvoiceGroupFormField,
  InvoiceGroupSelectItem,
  useSidebar,
  ErrorComponents,
  InfoPanel,
} from '@css-xp/base';
import { updateEntitlementPlan } from '@atlassian/temp-proxies';
import { OMSMetadata } from '@atlassian/cssxp-types-entitlement';
import { getAppropriatePaidPromotions } from '@atlassian/temp-helper';
// import { OpenSidebarLink } from '@css-xp/base';
import { OfferingSelectItem } from '../../change-plan/src/entitlement-change-plan-pane';
import { EntitlementDetails } from '@css-xp/graphtypes/src/generated-types-new';

enum TransitionTime {
  UNKNOWN = 'unknown',
  UNKNOWN_MESSAGE = ' - We were unable to determine if the successfully submitted order is immediate or scheduled.',
}

interface Props {
  entitlement: EntitlementDetails;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  transactionAccountContext: TransactionAccountContextType;
}

interface FormData {
  invoiceGroupId?: InvoiceGroupSelectItem;
  offeringSelectedItem?: OfferingSelectItem;
  supportTicketNumber?: string | null;
  selectedAddOns?: {
    selectedAddOn: AddOnSelectItem;
    quantity: string;
  }[];
}

const transactionAccountNotFoundError: Error = {
  name: 'Transaction account - not found',
  message: 'Unable to fetch transaction account',
};

const constructRequestData = (
  entitlement: EntitlementDetails,
  formData: FormData,
  formApi: FormApi<FormData>,
  productId: string
) => {
  // @atlaskit/form does not handle default form values well, creating thing like `selectedAddOns[0].selectedAddOn` instead of creating an object at that index. Lodash has our back.
  const transformedData: FormData = {};
  // setting key to any as getFieldState uses the actual keys of FormData, which does not include array indices
  formApi.getRegisteredFields().forEach((key: any) => {
    // get the value from the field state or the form data
    const value = formApi.getFieldState(key)?.value ?? get(formData, key);
    set(transformedData, key, value);
  });

  const formattedAddOnSubscriptionRequests: AddOns[] =
    transformedData.selectedAddOns
      ?.map((addOn) => ({
        offeringId: addOn.selectedAddOn.value.offeringId || '',
        units: addOn.quantity,
      }))
      .filter(isPresent) || [];

  const requestData: UpdateOrderRequest = {
    invoiceGroupId: transformedData?.invoiceGroupId?.value,
    offeringId: transformedData?.offeringSelectedItem?.value.id ?? '',
    entitlementId: entitlement.id,
    billingFrequency: BillingFrequency.MONTHLY,
    addOns: formattedAddOnSubscriptionRequests,
    metadata: {
      [OMSMetadata.SUPPORT_TICKET_NUMBER]: formData.supportTicketNumber ?? null,
    },
    productId: productId ?? '',
  };

  return requestData;
};

export const EntitlementMontlySubscriptionPane = ({ entitlement, refetch, transactionAccountContext }: Props) => {
  const { closeSidebar } = useSidebar();

  const { error: formError, isProcessing, submitForm, complete, data: response } = useFormSubmit<
    UpdateOrderRequest,
    any
  >({
    submitFormAction: updateEntitlementPlan,
    onComplete: () => {
      closeSidebar();
      refetch();
    },
  });

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  const { data, loading, error, getTransactionAccount } = transactionAccountContext;

  if (loading) return <LoadingIndicator />;

  const transactionAccount = getTransactionAccount(data);

  if (error && !transactionAccount) return <ErrorComponents.GenericErrorPage error={error} />;

  if (!transactionAccount) {
    return <ErrorComponents.GenericErrorPage error={transactionAccountNotFoundError} />;
  }

  const defaultBillingGroupId = entitlement?.activeSubscriptionBillingGroupId ?? entitlement?.billingGroupId;
  const availableInvoiceGroups = (transactionAccount?.availableInvoiceGroups?.nodes ?? []) as Maybe<InvoiceGroup[]>;
  const subscriptionId = entitlement?.subscriptionId ?? undefined;

  const handleFormSubmit = (formData: FormData, formApi: FormApi<FormData>) => {
    const requestData = constructRequestData(entitlement, formData, formApi, entitlement?.productId ?? '');
    submitForm(requestData);
  };

  if (complete && !notificationSent) {
    const transitionTimeError =
      response.transitionTime === TransitionTime.UNKNOWN ? TransitionTime.UNKNOWN_MESSAGE : undefined;

    const notificationOptions = orderSuccessNotification(
      'SWITCH_TO_MONTHLY',
      response.transitionTime,
      transitionTimeError,
      true
    );
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  }

  const hasPaymentMethod = transactionAccount?.availablePaymentMethods?.totalCount ?? false;
  const availablePromotions = entitlement.availablePromotions ?? [];
  const promotions = entitlement?.scheduledChanges?.promotions
    ? entitlement?.scheduledChanges?.promotions
    : entitlement.promotions ?? [];
  const paidMonthlyAvailablePromotions = getAppropriatePaidPromotions(availablePromotions, CycleInterval.MONTH);
  const paidMonthlyPromotions = getAppropriatePaidPromotions(promotions, CycleInterval.MONTH);

  return (
    <div className="entitlement-monthly-subscription-pane" style={{ maxWidth: '600px' }}>
      <Form onSubmit={handleFormSubmit}>
        {({ formProps }) => (
          <form {...formProps}>
            <FormHeader title="Switch to monthly subscription"></FormHeader>
            <FormSection>
              {formError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to switch to monthly subscription for the following reason"
                      message={formError}
                    />
                  }
                />
              )}
            </FormSection>
            <FormSection>
              {!hasPaymentMethod && (
                <InfoPanel
                  type="warning"
                  title="Cannot switch to monthly billing - entitlement has no billing details on file"
                  message={
                    <span>
                      This entitlement cannot be switched to monthly billing until there are billing details on file.{' '}
                      {/* <OpenSidebarLink
                                                sidebar={
                                                    Sidebar.GET_UPDATE_PAYMENT_METHOD_LINK
                                                }
                                                payload={{
                                                    updatePaymentMethodLink: updatePaymentMethodLink,
                                                }}
                                            >
                                                Get update payment link
                                            </OpenSidebarLink>{' '} */}
                      and ask the customer to add their credit card before processing this change.
                    </span>
                  }
                />
              )}
            </FormSection>
            <FormSection title="Product details">
              <StatuspageAvailableOfferingsFormField entitlement={entitlement} isEditDisabled={true} />
            </FormSection>
            <FormSection>
              <Field name="supportTicketNumber" label="Support ticket number">
                {({ fieldProps }) => <Textfield {...fieldProps} name="supportTicketNumber" />}
              </Field>
            </FormSection>

            <FormSection>
              <SelectPromotionFormField
                availablePromotions={paidMonthlyAvailablePromotions}
                promotions={paidMonthlyPromotions}
              />
            </FormSection>

            <FormSection>
              <InvoiceGroupFormField
                productId={entitlement?.productId ?? undefined}
                availableInvoiceGroups={availableInvoiceGroups}
                entitlementBillingInvoiceGroupId={entitlement?.billingGroupId}
                scheduledBillingInvoiceGroupId={entitlement?.scheduledChanges?.scheduledBillingInvoiceGroupId}
                isDisabled={!subscriptionId ? false : !!defaultBillingGroupId}
              />
            </FormSection>
            <FormFooter align="start">
              <ButtonGroup>
                <LoadingButton appearance="primary" type="submit" isLoading={isProcessing}>
                  Switch to monthly
                </LoadingButton>
                <Button appearance="subtle" onClick={() => closeSidebar()}>
                  Cancel
                </Button>
              </ButtonGroup>
            </FormFooter>
          </form>
        )}
      </Form>
    </div>
  );
};
